import React, { useState, useEffect, useRef, useMemo } from "react";
import { db } from "../firebase";
import {
  collection, addDoc, onSnapshot, query, orderBy,
  doc, updateDoc, deleteDoc, where
} from "firebase/firestore";
import axios from "axios";
import { sendNotification } from "./notify";

const CLOUD_NAME = "dscxfsvrf";
const UPLOAD_PRESET = "ficsori-portal";
const RAW_UPLOAD_PRESET = "ficsori-portal-raw";

const S = {
  btn:{fontSize:12,padding:"5px 12px",borderRadius:8,border:"0.5px solid #ccc",background:"#fff",cursor:"pointer",fontFamily:"sans-serif"},
  btnPrimary:{fontSize:12,padding:"5px 12px",borderRadius:8,border:"none",background:"#C0111F",color:"#fff",cursor:"pointer",fontFamily:"sans-serif"},
  btnSm:{fontSize:11,padding:"3px 8px",borderRadius:6,border:"0.5px solid #ccc",background:"#fff",cursor:"pointer"},
  input:{width:"100%",padding:"7px 10px",fontSize:13,border:"0.5px solid #ccc",borderRadius:8,fontFamily:"sans-serif",background:"#fff",boxSizing:"border-box"},
};

function fileIcon(name){
  const ext=(name||"").split(".").pop().toLowerCase();
  if(ext==="pdf") return "📄";
  if(["xlsx","xls","csv"].includes(ext)) return "📊";
  if(["doc","docx"].includes(ext)) return "📝";
  if(["jpg","jpeg","png","gif","webp"].includes(ext)) return "🖼";
  return "📎";
}

function formatSize(bytes){
  if(!bytes) return "";
  if(bytes<1024) return bytes+"B";
  if(bytes<1048576) return (bytes/1024).toFixed(1)+"KB";
  return (bytes/1048576).toFixed(1)+"MB";
}

function formatDate(ts){
  if(!ts) return "";
  return new Date(ts).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});
}

export default function ClientFileManager({ clientName, folderFilter, onClearFilter, userEmail }) {
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [renameFile, setRenameFile] = useState(null);
  const [renameFileName, setRenameFileName] = useState("");
  const [renameFolder, setRenameFolder] = useState(null);
  const [renameFolderName, setRenameFolderName] = useState("");
  const [moveFileModal, setMoveFileModal] = useState(null);
  const [batchMoveModal, setBatchMoveModal] = useState(false);
  const fileRef = useRef();
  const folderRef = useRef();
  const leftWidth = 220;

  useEffect(()=>{
    if(!clientName) return;
    const unsubFiles = onSnapshot(
      collection(db,"files"),
      snap=>setFiles(snap.docs.map(d=>({...d.data(),firestoreId:d.id})).filter(f=>f.client===clientName))
    );
    const unsubFolders = onSnapshot(
      collection(db,"folders"),
      snap=>setFolders(snap.docs.map(d=>({...d.data(),firestoreId:d.id})).filter(f=>f.client===clientName))
    );
    return ()=>{ unsubFiles(); unsubFolders(); };
  },[clientName]);

  // Apply folderFilter from task link
  useEffect(()=>{
    if(folderFilter){
      const f = folders.find(f=>f.name===folderFilter||f.firestoreId===folderFilter);
      if(f) setSelectedFolder(f);
    }
  },[folderFilter, folders]);

  const folderFiles = useMemo(()=>{
    let list = files;
    if(selectedFolder) list = list.filter(f=>f.folderId===selectedFolder.firestoreId);
    else if(!selectedFolder && folderFilter) list = list;
    if(search) list = list.filter(f=>(f.name||"").toLowerCase().includes(search.toLowerCase()));
    return [...list].sort((a,b)=>{
      let av,bv;
      if(sortBy==="name"){ av=(a.name||"").toLowerCase(); bv=(b.name||"").toLowerCase(); }
      else if(sortBy==="size"){ av=a.size||""; bv=b.size||""; }
      else if(sortBy==="date"){ av=a.uploadedAt||""; bv=b.uploadedAt||""; }
      else if(sortBy==="folder"){ av=(a.folderName||"").toLowerCase(); bv=(b.folderName||"").toLowerCase(); }
      if(av<bv) return sortDir==="asc"?-1:1;
      if(av>bv) return sortDir==="asc"?1:-1;
      return 0;
    });
  },[files, selectedFolder, search, sortBy, sortDir, folderFilter]);

  // Root folders only for tree
  const rootFolders = folders.filter(f=>!f.parentId);
  const subFolders = (parentId) => folders.filter(f=>f.parentId===parentId);

  const createFolder = async (name, parentId=null) => {
    if(!name.trim()) return;
    const parent = parentId ? folders.find(f=>f.firestoreId===parentId) : null;
    await addDoc(collection(db,"folders"),{
      name: name.trim(), client: clientName,
      parentId: parentId||null, parentName: parent?.name||null,
      createdAt: new Date().toISOString(),
    });
  };

  const deleteFolder = async (folder) => {
    if(!window.confirm(`Delete folder "${folder.name}"? Files inside will not be deleted.`)) return;
    await deleteDoc(doc(db,"folders",folder.firestoreId));
    if(selectedFolder?.firestoreId===folder.firestoreId) setSelectedFolder(null);
  };

  const saveRenameFolder = async () => {
    if(!renameFolderName.trim()) return;
    await updateDoc(doc(db,"folders",renameFolder.firestoreId),{name:renameFolderName.trim()});
    setRenameFolder(null);
  };

  const uploadFiles = async (fileList, folderOverride) => {
    setUploading(true);
    const activeFolder = folderOverride || selectedFolder;
    for(const file of Array.from(fileList)){
      try{
        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", "ficsori-portal");
        const sanitize = s => s.replace(/[^a-zA-Z0-9_\-]/g,"_").replace(/__+/g,"_");
        formData.append("folder", `ficsori-portal/${sanitize(clientName||"")}/${sanitize(activeFolder?.name||"misc")}`);
        setUploadProgress(p=>({...p,[file.name]:0}));
        const res = await axios.post(
          `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, formData,
          { onUploadProgress: e=>setUploadProgress(p=>({...p,[file.name]:Math.round(e.loaded*100/e.total)})) }
        );
        const fileData = {
          name: file.name, client: clientName,
          folderId: activeFolder?.firestoreId||null,
          folderName: activeFolder?.name||"Uncategorized",
          url: res.data.secure_url,
          size: formatSize(file.size),
          uploadedAt: new Date().toISOString(),
          uploadedBy: userEmail,
          uploadedByClient: true,
        };
        await addDoc(collection(db,"files"), fileData);
        setUploadProgress(p=>({...p,[file.name]:100}));
        // Send email notification to Frank
        sendNotification("client_uploaded",{
          recipientEmail: "frank@ficsoriaccounting.com",
          recipientName: "Frank Ficsori",
          clientName: clientName,
          details: `${clientName} uploaded: ${file.name} → ${selectedFolder?.name||"Uncategorized"}`,
        });
      }catch(e){ alert("Failed to upload "+file.name+": "+e.message); }
    }
    setUploading(false);
    setUploadProgress({});
  };

  const uploadFilesFromFolder = async (fileList, overrideFolderName) => {
    const files = Array.from(fileList);
    if(!files.length) return;
    const folderName = overrideFolderName || (files[0].webkitRelativePath ? files[0].webkitRelativePath.split('/')[0] : null);
    if(folderName && clientName){
      try{
        const newFolderRef = await addDoc(collection(db,"folders"),{
          name: folderName,
          client: clientName,
          parentId: selectedFolder?.firestoreId||null,
          parentName: selectedFolder?.name||null,
          createdAt: new Date().toISOString(),
        });
        setSelectedFolder({ firestoreId: newFolderRef.id, name: folderName });
        await uploadFiles(files, { firestoreId: newFolderRef.id, name: folderName });
        return;
      }catch(e){ console.error("Failed to create subfolder:", e); }
    }
    uploadFiles(files);
  };

  const deleteFile = async (file) => {
    if(!window.confirm(`Delete "${file.name}"?`)) return;
    await deleteDoc(doc(db,"files",file.firestoreId));
  };

  const batchDelete = async () => {
    if(!window.confirm(`Delete ${selectedFiles.size} file${selectedFiles.size!==1?"s":""}? This cannot be undone.`)) return;
    for(const id of selectedFiles){
      try{ await deleteDoc(doc(db,"files",id)); }catch(e){}
    }
    setSelectedFiles(new Set());
  };

  const moveFile = async (file, targetFolder) => {
    await updateDoc(doc(db,"files",file.firestoreId),{
      folderId: targetFolder?.firestoreId||null,
      folderName: targetFolder?.name||"Uncategorized",
    });
    setMoveFileModal(null);
  };

  const batchMove = async (targetFolder) => {
    for(const id of selectedFiles){
      try{ await updateDoc(doc(db,"files",id),{
        folderId: targetFolder?.firestoreId||null,
        folderName: targetFolder?.name||"Uncategorized",
      }); }catch(e){}
    }
    setSelectedFiles(new Set());
    setBatchMoveModal(false);
  };

  const saveRenameFile = async () => {
    if(!renameFileName.trim()) return;
    await updateDoc(doc(db,"files",renameFile.firestoreId),{name:renameFileName.trim()});
    setRenameFile(null);
  };

  const handleSort = (col) => {
    if(sortBy===col) setSortDir(d=>d==="asc"?"desc":"asc");
    else{ setSortBy(col); setSortDir("asc"); }
  };
  const sortIcon = (col) => sortBy===col?(sortDir==="asc"?" ↑":" ↓"):" ↕";

  const toggleSelect = (id) => setSelectedFiles(prev=>{ const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n; });
  const toggleAll = () => selectedFiles.size===folderFiles.length ? setSelectedFiles(new Set()) : setSelectedFiles(new Set(folderFiles.map(f=>f.firestoreId)));

  const FolderRow = ({folder, depth=0}) => {
    const [expanded, setExpanded] = useState(depth===0);
    const children = subFolders(folder.firestoreId);
    const isActive = selectedFolder?.firestoreId===folder.firestoreId;
    return (
      <div>
        <div style={{display:"flex",alignItems:"center",gap:4,padding:"5px 8px",borderRadius:6,cursor:"pointer",
          background:isActive?"#fdf0f0":"transparent",color:isActive?"#C0111F":"#555",
          paddingLeft:8+depth*14,fontSize:12,fontWeight:isActive?500:400}}
          onClick={()=>{ setSelectedFolder(folder); if(children.length) setExpanded(x=>!x); }}
          onMouseEnter={e=>!isActive&&(e.currentTarget.style.background="#f5f5f0")}
          onMouseLeave={e=>!isActive&&(e.currentTarget.style.background="transparent")}>
          {children.length>0&&<span style={{fontSize:10,color:"#aaa",width:12}}>{expanded?"▼":"▶"}</span>}
          {!children.length&&<span style={{width:12}}/>}
          <span style={{fontSize:14}}>📁</span>
          <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{folder.name}</span>
          <div style={{display:"flex",gap:2,opacity:0}} className="folder-actions"
            onMouseEnter={e=>e.currentTarget.style.opacity=1}
            onMouseLeave={e=>e.currentTarget.style.opacity=0}>
            <span title="Rename" onClick={e=>{e.stopPropagation();setRenameFolder(folder);setRenameFolderName(folder.name);}} style={{cursor:"pointer",fontSize:12,padding:"1px 4px"}}>✏</span>
            <span title="Delete" onClick={e=>{e.stopPropagation();deleteFolder(folder);}} style={{cursor:"pointer",fontSize:12,padding:"1px 4px",color:"#C0111F"}}>🗑</span>
          </div>
        </div>
        {expanded&&children.map(c=><FolderRow key={c.firestoreId} folder={c} depth={depth+1}/>)}
      </div>
    );
  };

  return (
    <div style={{display:"flex",flexDirection:"column",height:"calc(100vh - 160px)"}}>
      {/* Modals */}
      {showCreateFolder&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.35)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{background:"#fff",borderRadius:12,padding:"20px 24px",width:340}}>
            <div style={{fontSize:13,fontWeight:500,marginBottom:10}}>New Folder</div>
            <input style={S.input} value={newFolderName} onChange={e=>setNewFolderName(e.target.value)} placeholder="Folder name..." autoFocus
              onKeyDown={e=>{ if(e.key==="Enter"){ createFolder(newFolderName,selectedFolder?.firestoreId||null); setNewFolderName(""); setShowCreateFolder(false); } if(e.key==="Escape") setShowCreateFolder(false); }}/>
            <div style={{fontSize:11,color:"#888",marginTop:6,marginBottom:10}}>{selectedFolder?`Subfolder inside: ${selectedFolder.name}`:"Top-level folder"}</div>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button style={S.btn} onClick={()=>setShowCreateFolder(false)}>Cancel</button>
              <button style={S.btnPrimary} onClick={()=>{ createFolder(newFolderName,selectedFolder?.firestoreId||null); setNewFolderName(""); setShowCreateFolder(false); }}>Create</button>
            </div>
          </div>
        </div>
      )}
      {renameFile&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.35)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{background:"#fff",borderRadius:12,padding:"20px 24px",width:380}}>
            <div style={{fontSize:13,fontWeight:500,marginBottom:4}}>Rename File</div>
            <div style={{fontSize:11,color:"#888",marginBottom:10}}>Original: {renameFile.name}</div>
            <input style={S.input} value={renameFileName} onChange={e=>setRenameFileName(e.target.value)} autoFocus onKeyDown={e=>e.key==="Enter"&&saveRenameFile()}/>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:12}}>
              <button style={S.btn} onClick={()=>setRenameFile(null)}>Cancel</button>
              <button style={S.btnPrimary} onClick={saveRenameFile}>Save</button>
            </div>
          </div>
        </div>
      )}
      {renameFolder&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.35)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{background:"#fff",borderRadius:12,padding:"20px 24px",width:340}}>
            <div style={{fontSize:13,fontWeight:500,marginBottom:10}}>Rename Folder</div>
            <input style={S.input} value={renameFolderName} onChange={e=>setRenameFolderName(e.target.value)} autoFocus onKeyDown={e=>e.key==="Enter"&&saveRenameFolder()}/>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:12}}>
              <button style={S.btn} onClick={()=>setRenameFolder(null)}>Cancel</button>
              <button style={S.btnPrimary} onClick={saveRenameFolder}>Save</button>
            </div>
          </div>
        </div>
      )}
      {moveFileModal&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.35)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{background:"#fff",borderRadius:14,padding:"20px 24px",width:380,maxHeight:"70vh",display:"flex",flexDirection:"column"}}>
            <div style={{fontSize:13,fontWeight:500,marginBottom:4}}>Move File</div>
            <div style={{fontSize:11,color:"#888",marginBottom:12}}>{moveFileModal.name}</div>
            <div style={{flex:1,overflowY:"auto",border:"0.5px solid #e0dfd8",borderRadius:8,marginBottom:12}}>
              <div onClick={()=>moveFile(moveFileModal,null)} style={{padding:"10px 14px",cursor:"pointer",fontSize:12,borderBottom:"0.5px solid #f5f5f0"}}
                onMouseEnter={e=>e.currentTarget.style.background="#f5f5f0"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                📂 Root (no folder)
              </div>
              {folders.map(f=>(
                <div key={f.firestoreId} onClick={()=>moveFile(moveFileModal,f)}
                  style={{padding:"10px 14px",cursor:"pointer",fontSize:12,borderBottom:"0.5px solid #f5f5f0",paddingLeft:f.parentId?"28px":"14px",
                    color:moveFileModal.folderId===f.firestoreId?"#C0111F":"#555",fontWeight:moveFileModal.folderId===f.firestoreId?500:400}}
                  onMouseEnter={e=>e.currentTarget.style.background="#f5f5f0"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  📁 {f.name}{f.parentId&&<span style={{fontSize:10,color:"#aaa",marginLeft:6}}>subfolder</span>}
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button style={S.btn} onClick={()=>setMoveFileModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {batchMoveModal&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.35)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{background:"#fff",borderRadius:14,padding:"20px 24px",width:380,maxHeight:"70vh",display:"flex",flexDirection:"column"}}>
            <div style={{fontSize:13,fontWeight:500,marginBottom:12}}>Move {selectedFiles.size} files to...</div>
            <div style={{flex:1,overflowY:"auto",border:"0.5px solid #e0dfd8",borderRadius:8,marginBottom:12}}>
              <div onClick={()=>batchMove(null)} style={{padding:"10px 14px",cursor:"pointer",fontSize:12,borderBottom:"0.5px solid #f5f5f0"}}
                onMouseEnter={e=>e.currentTarget.style.background="#f5f5f0"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                📂 Root (no folder)
              </div>
              {folders.map(f=>(
                <div key={f.firestoreId} onClick={()=>batchMove(f)}
                  style={{padding:"10px 14px",cursor:"pointer",fontSize:12,borderBottom:"0.5px solid #f5f5f0",paddingLeft:f.parentId?"28px":"14px"}}
                  onMouseEnter={e=>e.currentTarget.style.background="#f5f5f0"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  📁 {f.name}
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button style={S.btn} onClick={()=>setBatchMoveModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexShrink:0}}>
        <div style={{fontSize:13,fontWeight:500}}>My Documents</div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <input placeholder="Search files..." value={search} onChange={e=>setSearch(e.target.value)}
            style={{...S.input,width:180,fontSize:11}}/>
          <button style={S.btn} onClick={()=>setShowCreateFolder(true)}>📁 New Folder</button>
          <button style={S.btnPrimary} onClick={()=>fileRef.current.click()} disabled={uploading}>
            {uploading?"Uploading...":"⬆ Upload Files/Folders"}
          </button>
          <input ref={fileRef} type="file" multiple style={{display:"none"}} onChange={e=>{ uploadFiles(e.target.files); e.target.value=""; }}/>
          <input ref={folderRef} type="file" webkitdirectory="" style={{display:"none"}} onChange={e=>{ uploadFilesFromFolder(e.target.files); e.target.value=""; }}/>
        </div>
      </div>

      {/* Upload progress */}
      {Object.keys(uploadProgress).length>0&&(
        <div style={{marginBottom:8,flexShrink:0}}>
          {Object.entries(uploadProgress).map(([name,pct])=>(
            <div key={name} style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,fontSize:11}}>
              <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{name}</span>
              <div style={{width:120,height:6,background:"#f0efe8",borderRadius:3}}>
                <div style={{width:pct+"%",height:"100%",background:"#C0111F",borderRadius:3,transition:"width 0.3s"}}/>
              </div>
              <span style={{width:30,color:"#888"}}>{pct}%</span>
            </div>
          ))}
        </div>
      )}

      {/* Main panel */}
      <div style={{display:"flex",flex:1,border:"0.5px solid #e0dfd8",borderRadius:12,overflow:"hidden",background:"#fff"}}>

        {/* Left — folder tree */}
        <div style={{width:leftWidth,minWidth:leftWidth,borderRight:"0.5px solid #e0dfd8",display:"flex",flexDirection:"column",overflow:"hidden",flexShrink:0}}>
          <div style={{padding:"8px 6px",flex:1,overflowY:"auto"}}>
            {folders.length===0&&(
              <div style={{fontSize:11,color:"#aaa",textAlign:"center",padding:"16px 8px"}}>
                <div>No folders yet</div>
                <button style={{...S.btnSm,fontSize:10,marginTop:6}} onClick={()=>setShowCreateFolder(true)}>+ Create folder</button>
              </div>
            )}
            <div onClick={()=>setSelectedFolder(null)}
              style={{display:"flex",alignItems:"center",gap:6,padding:"5px 8px",borderRadius:6,cursor:"pointer",
                background:!selectedFolder?"#fdf0f0":"transparent",color:!selectedFolder?"#C0111F":"#555",
                fontWeight:!selectedFolder?500:400,fontSize:12,marginBottom:2}}
              onMouseEnter={e=>selectedFolder&&(e.currentTarget.style.background="#f5f5f0")}
              onMouseLeave={e=>selectedFolder&&(e.currentTarget.style.background="transparent")}>
              <span style={{fontSize:14}}>📂</span> All Files
            </div>
            {rootFolders.map(f=><FolderRow key={f.firestoreId} folder={f}/>)}
          </div>
        </div>

        {/* Right — file list */}
        <div style={{display:"flex",flexDirection:"column",overflow:"hidden",flex:1}}>

          {/* Batch action bar */}
          {selectedFiles.size>0&&(
            <div style={{background:"#1a1a18",padding:"8px 12px",display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
              <span style={{fontSize:12,color:"#fff",fontWeight:500}}>{selectedFiles.size} file{selectedFiles.size!==1?"s":""} selected</span>
              <div style={{flex:1}}/>
              <button style={{...S.btnSm,background:"#534AB7",color:"#fff",border:"none",fontSize:11}} onClick={()=>setBatchMoveModal(true)}>⇄ Move</button>
              <button style={{...S.btnSm,background:"#A32D2D",color:"#fff",border:"none",fontSize:11}} onClick={batchDelete}>🗑 Delete</button>
              <button style={{...S.btnSm,color:"#aaa",fontSize:11}} onClick={()=>setSelectedFiles(new Set())}>✕ Clear</button>
            </div>
          )}

          {/* Drag & drop overlay */}
          <div
            onDragOver={e=>{e.preventDefault();}}
            onDrop={async e=>{
              e.preventDefault();
              const items = Array.from(e.dataTransfer.items);
              const allFiles = [];
              let detectedFolderName = null;
              const readEntry = (entry) => new Promise(resolve => {
                if(entry.isFile){ entry.file(f=>{ allFiles.push(f); resolve(); }, ()=>resolve()); }
                else if(entry.isDirectory){
                  const reader = entry.createReader();
                  const readAll = (cb) => { reader.readEntries(async entries => { if(entries.length===0){cb();return;} await Promise.all(entries.map(readEntry)); readAll(cb); }); };
                  readAll(resolve);
                } else { resolve(); }
              });
              const entries = items.map(i=>i.webkitGetAsEntry()).filter(Boolean);
              if(entries.length===1 && entries[0].isDirectory) detectedFolderName = entries[0].name;
              await Promise.all(entries.map(readEntry));
              if(allFiles.length > 0){
                if(detectedFolderName) uploadFilesFromFolder(allFiles, detectedFolderName);
                else uploadFiles(allFiles);
              }
            }}
            style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}
          >
        {/* File table */}
          {folderFiles.length===0?(
            <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",color:"#aaa",gap:10}}>
              <div style={{fontSize:32}}>📂</div>
              <div style={{fontSize:13}}>{selectedFolder?`No files in "${selectedFolder.name}"`:"No files yet"}</div>
              <div style={{display:"flex",gap:8,justifyContent:"center"}}>
                <button style={S.btnPrimary} onClick={()=>fileRef.current.click()}>📄 Browse Files</button>
                <button style={{...S.btn,background:"#E6F1FB",color:"#185FA5",border:"none"}} onClick={()=>folderRef.current.click()}>📁 Browse Folder</button>
              </div>
            </div>
          ):(
            <div style={{flex:1,overflowY:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead><tr style={{background:"#fafaf8"}}>
                  <th style={{padding:"7px 10px",borderBottom:"0.5px solid #e0dfd8",width:32}}>
                    <input type="checkbox" checked={selectedFiles.size===folderFiles.length&&folderFiles.length>0} onChange={toggleAll}/>
                  </th>
                  {[["name","File"],["folder","Folder"],["size","Size"],["date","Uploaded"]].map(([col,label])=>(
                    <th key={col} onClick={()=>handleSort(col)} style={{textAlign:"left",padding:"7px 12px",borderBottom:"0.5px solid #e0dfd8",color:"#888",fontWeight:500,fontSize:11,cursor:"pointer",userSelect:"none",whiteSpace:"nowrap"}}>
                      {label}{sortIcon(col)}
                    </th>
                  ))}
                  <th style={{padding:"7px 12px",borderBottom:"0.5px solid #e0dfd8"}}></th>
                </tr></thead>
                <tbody>
                  {folderFiles.map(f=>(
                    <tr key={f.firestoreId} style={{borderBottom:"0.5px solid #f5f5f0",background:selectedFiles.has(f.firestoreId)?"#fdf8f8":"transparent"}}>
                      <td style={{padding:"8px 10px",width:32}}>
                        <input type="checkbox" checked={selectedFiles.has(f.firestoreId)} onChange={()=>toggleSelect(f.firestoreId)}/>
                      </td>
                      <td style={{padding:"8px 12px"}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <span style={{fontSize:16,flexShrink:0}}>{fileIcon(f.name)}</span>
                          <div style={{fontWeight:500,fontSize:12,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:200}}>{f.name}</div>
                        </div>
                      </td>
                      <td style={{padding:"8px 12px",color:"#888",fontSize:11}}>{f.folderName||"—"}</td>
                      <td style={{padding:"8px 12px",color:"#aaa",fontSize:11}}>{f.size}</td>
                      <td style={{padding:"8px 12px",color:"#aaa",fontSize:11,whiteSpace:"nowrap"}}>{formatDate(f.uploadedAt)}</td>
                      <td style={{padding:"8px 12px"}}>
                        <div style={{display:"flex",gap:4}}>
                          <a title="View file" href={f.url} target="_blank" rel="noreferrer"
                            style={{...S.btnSm,textDecoration:"none",color:"#1a1a18",fontSize:11,padding:"2px 8px",display:"inline-flex",alignItems:"center"}}>👁</a>
                          <a title="Download" href={f.url} download={f.name} target="_blank" rel="noreferrer"
                            style={{...S.btnSm,textDecoration:"none",color:"#1a1a18",fontSize:11,padding:"2px 8px",display:"inline-flex",alignItems:"center"}}>↓</a>
                          <button title="Rename" style={{...S.btnSm,fontSize:11,padding:"2px 8px"}} onClick={()=>{setRenameFile(f);setRenameFileName(f.name);}}>✏</button>
                          <button title="Move to folder" style={{...S.btnSm,fontSize:11,padding:"2px 8px",color:"#534AB7",borderColor:"#C5C3F0"}} onClick={()=>setMoveFileModal(f)}>⇄</button>
                          <button title="Delete" style={{...S.btnSm,color:"#A32D2D",borderColor:"#FCEBEB",background:"#FCEBEB",fontSize:11,padding:"2px 8px"}} onClick={()=>deleteFile(f)}>Del</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}
